
import { ReactNode } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { useAuth } from '@/hooks/useAuth';
import { 
  SidebarProvider,
  Sidebar,
  SidebarContent,
  SidebarGroup,
  SidebarGroupLabel,
  SidebarGroupContent,
  SidebarMenu,
  SidebarMenuItem,
  SidebarMenuButton,
  SidebarHeader,
  SidebarFooter,
  SidebarSeparator,
  useSidebar,
} from '@/components/ui/sidebar';
import { 
  Users, 
  Settings, 
  LayoutDashboard, 
  LogOut, 
  Home,
  FileText,
  Image,
  BarChart3,
  Trophy,
  Layout,
  FolderOpen,
  ChevronLeft,
  ChevronRight,
  Bell,
  Dumbbell,
  ClipboardList,
  Phone
} from 'lucide-react';
import { useTranslation } from 'react-i18next';
import AdminSearch from '@/components/admin/AdminSearch';
import AccountMenu from '@/components/admin/AccountMenu';

interface AdminLayoutProps {
  children: ReactNode;
}

export default function AdminLayout({ children }: AdminLayoutProps) {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, userRole, signOut } = useAuth();
  const { t } = useTranslation();

  const handleSignOut = async () => {
    try {
      await signOut();
      // Clear user state and force navigation to auth page
      window.location.href = '/auth';
    } catch (error) {
      console.error('Logout error:', error);
      // Even if signOut fails, force redirect to auth page
      window.location.href = '/auth';
    }
  };

  // Toggle button for collapsing/expanding the sidebar with clear arrow icon
  const SidebarToggleButton = () => {
    const { state, toggleSidebar } = useSidebar();
    const isCollapsed = state === 'collapsed';
    return (
      <Button
        variant="ghost"
        size="icon"
        className="ml-1 h-8 w-8 shrink-0"
        aria-label={isCollapsed ? 'Sidebar einblenden' : 'Sidebar einklappen'}
        onClick={toggleSidebar}
      >
        {isCollapsed ? (
          <ChevronRight className="h-4 w-4" />
        ) : (
          <ChevronLeft className="h-4 w-4" />
        )}
      </Button>
    );
  };

  const menuItems = [
    {
      path: '/admin',
      label: t('admin.dashboard'),
      icon: LayoutDashboard,
    },
    {
      path: '/admin/users',
      label: t('admin.users'),
      icon: Users,
    },
    {
      path: '/admin/training',
      label: t('admin.training'),
      icon: Dumbbell,
    },
    {
      path: '/admin/training-requests',
      label: 'Anfragen',
      icon: ClipboardList,
    },
    {
      path: '/admin/blog',
      label: t('admin.blog'),
      icon: FileText,
    },
    {
      path: '/admin/events',
      label: t('admin.events'),
      icon: Layout,
    },
    {
      path: '/admin/media',
      label: t('admin.media'),
      icon: Image,
    },
    {
      path: '/admin/tournaments',
      label: t('admin.tournaments'),
      icon: Trophy,
    },
    {
      path: '/admin/files',
      label: t('admin.files'),
      icon: FolderOpen,
    },
    {
      path: '/admin/banner',
      label: t('admin.banner'),
      icon: Bell,
    },
    {
      path: '/admin/about',
      label: 'Über uns',
      icon: Users,
    },
    {
      path: '/admin/contact',
      label: 'Kontakt',
      icon: Phone,
    },
    {
      path: '/admin/analytics',
      label: t('admin.analytics'),
      icon: BarChart3,
    },
    {
      path: '/admin/settings',
      label: t('admin.settings'),
      icon: Settings,
    },
  ];

  return (
    <SidebarProvider>
      <div className="min-h-screen w-full min-w-0 max-w-full overflow-x-hidden bg-gradient-to-br from-background via-background to-secondary/20">
        <div className="flex w-full min-w-0">
          {/* Sidebar */}
          <Sidebar className="w-64 bg-card/95 backdrop-blur-sm border-r border-border/50">
            <SidebarHeader>
              <div className="relative mb-4 p-4 rounded-lg overflow-hidden">
                {/* Background Image */}
                <img
                  src="/lovable-uploads/df7a83c0-8d4c-488e-9ff0-aabf910cc91f.png"
                  alt="ASC Fechten Wien Logo"
                  className="absolute inset-0 w-full h-full object-cover opacity-20"
                  loading="lazy"
                />
                {/* Content */}
                <div className="relative z-10">
                  <h1 className="text-xl font-bold text-foreground">{t('admin.adminPanel')}</h1>
                  <p className="text-sm text-muted-foreground">Akademischer Sportclub Fechten CMS</p>
                </div>
              </div>
            </SidebarHeader>

            <SidebarContent>
              <SidebarGroup>
                <SidebarGroupLabel>{t('admin.navigation')}</SidebarGroupLabel>
                <SidebarGroupContent>
                  <SidebarMenu>
                    {menuItems.map((item) => {
                      const Icon = item.icon;
                      const isActive = location.pathname === item.path;

                      // Verstecke bestimmte Bereiche für Moderatoren
                      if (userRole === 'moderator') {
                        const moderatorRestrictedPaths = ['/admin/users', '/admin/settings'];
                        if (moderatorRestrictedPaths.includes(item.path)) {
                          return null;
                        }
                      }

                      return (
                        <SidebarMenuItem key={item.path}>
                          <SidebarMenuButton asChild isActive={isActive} tooltip={item.label}>
                            <Link to={item.path} className="justify-start gap-3">
                              <Icon className="h-4 w-4" />
                              <span>{item.label}</span>
                            </Link>
                          </SidebarMenuButton>
                        </SidebarMenuItem>
                      );
                    })}
                  </SidebarMenu>
                </SidebarGroupContent>
              </SidebarGroup>
            </SidebarContent>

            <SidebarSeparator />

            <SidebarFooter>
              <div className="space-y-2">
                <Link to="/">
                  <Button variant="outline" className="w-full justify-start gap-3 h-10">
                    <Home className="h-4 w-4" />
                    <span>{t('admin.backToWebsite')}</span>
                  </Button>
                </Link>
                <Button
                  variant="outline"
                  className="w-full justify-start gap-3 h-10 text-destructive hover:text-destructive"
                  onClick={handleSignOut}
                >
                  <LogOut className="h-4 w-4" />
                  <span>{t('admin.logout')}</span>
                </Button>
              </div>
            </SidebarFooter>
          </Sidebar>

          {/* Main Content Area with Header */}
          <div className="flex-1 flex flex-col min-w-0">
            <header className="sticky top-0 z-50 h-12 flex items-center gap-2 border-b border-border/50 px-2 bg-background/95 backdrop-blur-sm">
              <SidebarToggleButton />
              <span className="hidden sm:inline text-sm text-muted-foreground">Admin</span>
              <div className="ml-auto flex-1 sm:flex-none flex items-center justify-end gap-2 min-w-0">
                <AdminSearch />
                <AccountMenu />
              </div>
            </header>
            
            <main className="admin-main flex-1 p-3 sm:p-4 md:p-6 min-w-0 max-w-full overflow-x-hidden">
              {children}
            </main>
          </div>
        </div>
      </div>
    </SidebarProvider>
  );
}
